import { TeaProduct } from '@/types/common'

const productGallery = (productId: string): string[] =>
  Array.from({ length: 5 }, (_, index) => `/mock/products/${productId}-${String(index + 1).padStart(2, '0')}.webp`)

export const OFFICIAL_PRODUCT_IMAGE_GALLERIES = {
  'g-gift-sets': productGallery('g-gift-sets'),
  'h-tea-bags': productGallery('h-tea-bags'),
  'h-loose-leaf': productGallery('h-loose-leaf'),
  'i-tea-bags': productGallery('i-tea-bags'),
  'i-loose-leaf': productGallery('i-loose-leaf'),
  'm-tea-bags': productGallery('m-tea-bags'),
  'm-loose-leaf': productGallery('m-loose-leaf'),
  's-tea-bags': productGallery('s-tea-bags'),
  's-loose-leaf': productGallery('s-loose-leaf'),
} as const

const officialImagesFor = (routeId: keyof typeof OFFICIAL_PRODUCT_IMAGE_GALLERIES) =>
  OFFICIAL_PRODUCT_IMAGE_GALLERIES[routeId]

export const TEA_PRODUCT_NAMES = {
  i: { zh: '灣島紅玉', en: 'Island Ruby' },
  h: { zh: '冬藏蜜蘊', en: 'Honey Petal' },
  m: { zh: '山渺烏龍', en: 'Mountain Mist' },
  s: { zh: '冬祈春芽', en: 'Spring Sprout' },
  g: { zh: '禮盒', en: 'Gift Set' },
} as const

export const TEA_CATEGORIES = {
  'black-tea': { zh: '紅茶', en: 'Black Tea' },
  'oolong-tea': { zh: '烏龍茶', en: 'Oolong Tea' },
  'green-tea': { zh: '綠茶', en: 'Green Tea' },
} as const

export const TEA_FORMATS = {
  'loose-leaf': { zh: '散茶', en: 'Loose Leaf' },
  'tea-bags': { zh: '茶包', en: 'Tea Bags' },
  'gift-sets': { zh: '禮盒組', en: 'Gift Sets' },
} as const

export const PRODUCT_DETAIL_COPY = {
  'i-loose-leaf': {
    tagline: {
      en: 'An exuberance island of natural ruby tea tree',
      zh: '一座自然紅玉茶樹的馥郁島嶼',
    },
    intro: {
      en: 'Quench with water, fusing out a clear burning red tea soup.',
      zh: '以水喚醒茶葉，釋放清澈而明亮的紅茶湯色。',
    },
    summary: {
      en: 'The Island Ruby tea is fully oxidized and unroasted. It offers inviting notes of tangerine and malt, complemented by signature hints of cinnamon, resulting in a complex profile with a subtle caramel-like sweetness, minimal astringency and a refreshing aftertaste.',
      zh: '灣島紅玉為全發酵、未焙火紅茶，帶有柑橘、麥芽與紅玉品種特有的肉桂香氣。茶湯層次細緻，微帶焦糖般甜感，澀度低且回韻清爽。',
    },
    description: {
      en: 'The Island Ruby tea is an authentic black tea from Taiwan. Made from the Ruby 18 cultivar, also known as TTES No. 18 or Red Jade, this fully oxidized tea has a classic, full-bodied character with bright notes of tangerine and hints of cinnamon. Whether you enjoy it hot or iced, our tea brings the ambiance of a cozy teahouse right to your home.',
      zh: '灣島紅玉是來自台灣的正統紅茶，採用台茶 18 號，又稱紅玉製成。全發酵帶來飽滿茶體，並呈現明亮柑橘、麥芽與細緻肉桂香。無論熱飲或冰飲，都能把台灣茶館般的安定氛圍帶進日常。',
    },
    specs: ['Tea Type | TTES. NO.18 / Black tea', 'Process | Fully oxidized', 'Format | Loose leaf 75g'],
    tags: ['Light', 'Special'],
  },
  default: {
    tagline: {
      en: 'Taiwan tea for a calmer daily ritual',
      zh: '為日常儀式而生的台灣茶',
    },
    intro: {
      en: 'A carefully sourced tea selection from Taiwan, made for quiet moments and everyday balance.',
      zh: '嚴選自台灣的茶品，適合安靜片刻與日常平衡。',
    },
    summary: {
      en: 'Our tea products all originated from Taiwan and are selected for clarity, aroma, and a comforting finish. Each format is prepared for a different rhythm, from focused brewing to convenient daily cups.',
      zh: '我們的茶皆源自台灣，重視乾淨茶湯、香氣與舒適尾韻。不同包裝形式對應不同生活節奏，從專注沖泡到日常便利飲用皆可自然融入。',
    },
    description: {
      en: 'Clouty Space works with trusted Taiwanese tea sources to create products that feel calm, traceable, and easy to return to. Brew it as a focused ritual or keep it close for a steady daily cup.',
      zh: 'Clouty Space 與可信任的台灣茶來源合作，打造安心、可追溯且適合反覆回到日常的茶品。你可以把它當成專注儀式，也可以作為每天穩定的一杯茶。',
    },
    specs: ['Origin | Taiwan', 'Source | Direct tea farm selection'],
    tags: ['Taiwan', 'Daily Ritual'],
  },
} as const

export const SHOP_PRODUCTS: TeaProduct[] = [
  // Gift Set
  {
    id: 'g-gift-sets',
    key: 'g',
    price: 90.0,
    priceTwd: 2880,
    image: officialImagesFor('g-gift-sets')[0],
    images: officialImagesFor('g-gift-sets'),
    category: 'gift-sets',
    format: 'gift-sets',
    hasVariants: true,
  },
  // Honey Petal - Tea Bags
  {
    id: 'h-tea-bags',
    key: 'h',
    price: 21.0,
    priceTwd: 680,
    image: officialImagesFor('h-tea-bags')[0],
    images: officialImagesFor('h-tea-bags'),
    category: 'black-tea',
    format: 'tea-bags',
    hasVariants: false,
  },
  // Honey Petal - Loose Leaf
  {
    id: 'h-loose-leaf',
    key: 'h',
    price: 26.0,
    priceTwd: 840,
    image: officialImagesFor('h-loose-leaf')[0],
    images: officialImagesFor('h-loose-leaf'),
    category: 'black-tea',
    format: 'loose-leaf',
    hasVariants: false,
  },
  // Island Ruby - Tea Bags
  {
    id: 'i-tea-bags',
    key: 'i',
    price: 23.0,
    priceTwd: 740,
    image: officialImagesFor('i-tea-bags')[0],
    images: officialImagesFor('i-tea-bags'),
    category: 'black-tea',
    format: 'tea-bags',
    hasVariants: false,
  },
  // Island Ruby - Loose Leaf
  {
    id: 'i-loose-leaf',
    key: 'i',
    price: 30.0,
    priceTwd: 960,
    image: officialImagesFor('i-loose-leaf')[0],
    images: officialImagesFor('i-loose-leaf'),
    category: 'black-tea',
    format: 'loose-leaf',
    hasVariants: false,
  },
  // Mountain Mist - Tea Bags
  {
    id: 'm-tea-bags',
    key: 'm',
    price: 21.0,
    priceTwd: 680,
    image: officialImagesFor('m-tea-bags')[0],
    images: officialImagesFor('m-tea-bags'),
    category: 'oolong-tea',
    format: 'tea-bags',
    hasVariants: false,
  },
  // Mountain Mist - Loose Leaf
  {
    id: 'm-loose-leaf',
    key: 'm',
    price: 43.5,
    priceTwd: 1390,
    image: officialImagesFor('m-loose-leaf')[0],
    images: officialImagesFor('m-loose-leaf'),
    category: 'oolong-tea',
    format: 'loose-leaf',
    hasVariants: false,
  },
  // Spring Sprout - Tea Bags
  {
    id: 's-tea-bags',
    key: 's',
    price: 21.0,
    priceTwd: 680,
    image: officialImagesFor('s-tea-bags')[0],
    images: officialImagesFor('s-tea-bags'),
    category: 'green-tea',
    format: 'tea-bags',
    hasVariants: false,
  },
  // Spring Sprout - Loose Leaf
  {
    id: 's-loose-leaf',
    key: 's',
    price: 43.5,
    priceTwd: 1390,
    image: officialImagesFor('s-loose-leaf')[0],
    images: officialImagesFor('s-loose-leaf'),
    category: 'green-tea',
    format: 'loose-leaf',
    hasVariants: false,
  },
]

export const TEA_PRODUCTS: TeaProduct[] = [
  {
    id: 'i-loose-leaf',
    key: 'i',
    price: 30.0,
    priceTwd: 960,
    image: officialImagesFor('i-loose-leaf')[0],
    images: officialImagesFor('i-loose-leaf'),
  },
  {
    id: 's-loose-leaf',
    key: 's',
    price: 43.5,
    priceTwd: 1390,
    image: officialImagesFor('s-loose-leaf')[0],
    images: officialImagesFor('s-loose-leaf'),
  },
  {
    id: 'h-loose-leaf',
    key: 'h',
    price: 26.0,
    priceTwd: 840,
    image: officialImagesFor('h-loose-leaf')[0],
    images: officialImagesFor('h-loose-leaf'),
  },
  {
    id: 'm-loose-leaf',
    key: 'm',
    price: 43.5,
    priceTwd: 1390,
    image: officialImagesFor('m-loose-leaf')[0],
    images: officialImagesFor('m-loose-leaf'),
  },
]

export const SOCIAL_LINKS = {
  facebook: 'https://www.facebook.com/profile.php?id=100072498547679#',
  instagram: 'https://www.instagram.com/cloutyspace/',
} as const
